import { AlertCircle, Loader2, QrCode, RefreshCw } from "lucide-react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useActivities } from "@/hooks/analytics";

import type { Activity } from "@/types/activity";

interface Props {
  agentId: string;
  brand?: string;
}

const ScanActivityList = ({ agentId, brand = "#4E9CFF" }: Props) => {
  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useActivities({ action: "qr_scanned", resourceId: agentId });

  const activities: Activity[] = data?.pages.flatMap((p) => p.activities) ?? [];
  const total = data?.pages[0]?.count ?? activities.length;

  if (isLoading) {
    return (
      <Card className="flex items-center justify-center gap-2 p-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" style={{ color: brand }} />
        Loading scan activity…
      </Card>
    );
  }

  // A failed request is not the same as "nobody scanned" — never conflate them.
  if (isError) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-600" />
        <p className="text-sm font-semibold text-gray-900">We couldn't load scan activity</p>
        <p className="mt-1 text-sm text-gray-600">
          This doesn't mean nobody scanned — we just couldn't reach the record.
        </p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Try again
        </Button>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card className="p-8 text-center">
        <QrCode className="mx-auto mb-2 h-6 w-6" style={{ color: brand }} />
        <p className="text-sm font-semibold text-gray-900">No scans recorded yet</p>
        <p className="mt-1 text-sm text-gray-600">
          Every scan of this Genie's QR code shows up here — the first one usually lands the day your printed
          pieces go up.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card className="divide-y divide-gray-100">
        {activities.map((activity) => (
          <div key={activity.id} className="flex items-center justify-between p-4 gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{activity.title}</p>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                {activity.ip_address && <span className="text-xs text-gray-400">{activity.ip_address}</span>}
                {activity.metadata?.device && (
                  <span className="text-xs text-gray-400 capitalize">{activity.metadata.device}</span>
                )}
                {activity.metadata?.platform && (
                  <span className="text-xs text-gray-400">{activity.metadata.platform}</span>
                )}
                {activity.metadata?.timezone && (
                  <span className="text-xs text-gray-400">{activity.metadata.timezone}</span>
                )}
                {activity.metadata?.consumerEmail && (
                  <span className="text-xs text-gray-400 font-mono truncate max-w-[160px]">
                    <CopyableEmail email={activity.metadata.consumerEmail} />
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-400 shrink-0">{new Date(activity.created_at).toLocaleString()}</p>
          </div>
        ))}
      </Card>

      {/* Without this the list silently stops at the first page. */}
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-gray-500">
          Showing {activities.length} of {total} scans
        </span>
        {hasNextPage && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Show more
          </Button>
        )}
      </div>
    </div>
  );
};

export default ScanActivityList;
